import {
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
  // @ts-expect-error - no TypeScript definitions yet
} from 'src/components/CustomButton';
import { CustomTypography, TYPOGRAPHY_VARIANT } from 'src/components/CustomTypography';
import type { BackendOAuthConsentRequest } from 'src/types/backend';

import { ClientCapabilities } from '../../components/ClientCapabilities';
import { ClientHeader } from '../../components/ClientHeader';
import { ConsentLayout } from '../../components/ConsentLayout';
import { getOAuthConsentStopReasonMessage, OAUTH_CONSENT_DECISION } from '../../lib';
import { ViewWorkspacePicker } from '../ViewWorkspacePicker';

import { useHandlers } from './useHandlers';
import { useViewState } from './useViewState';
import styles from './ViewClientConsent.module.scss';

interface ViewClientConsentProps {
  className?: string;
  consentRequest: BackendOAuthConsentRequest;
}

export const ViewClientConsent = ({ className, consentRequest }: ViewClientConsentProps) => {
  const { selectedWorkspaceId, pendingDecision } = useViewState();
  const { handleAuthorize, handleCancel } = useHandlers();
  const { client, redirectHost, stopReason, workspaces } = consentRequest;

  const cancelButton = (
    <CustomButton
      fullWidth
      variant={CUSTOM_BUTTON_VARIANT.outlined}
      loading={pendingDecision === OAUTH_CONSENT_DECISION.deny}
      disabled={pendingDecision !== null}
      onClick={handleCancel}
      data-testid="oauthConsentCancelButton"
    >
      Cancel
    </CustomButton>
  );

  if (stopReason) {
    return (
      <ConsentLayout className={className}>
        <ClientHeader
          client={client}
          redirectHost={redirectHost}
        />
        <CustomTypography
          className={styles.stopMessage}
          variant={TYPOGRAPHY_VARIANT.regular14}
          data-testid="oauthConsentStopMessage"
        >
          {getOAuthConsentStopReasonMessage(stopReason)}
        </CustomTypography>
        {cancelButton}
      </ConsentLayout>
    );
  }

  return (
    <ConsentLayout className={className}>
      <ClientHeader
        client={client}
        redirectHost={redirectHost}
      />
      <ClientCapabilities clientName={client.name} />
      <ViewWorkspacePicker workspaces={workspaces} />
      <div className={styles.actions}>
        <CustomButton
          fullWidth
          loading={pendingDecision === OAUTH_CONSENT_DECISION.approve}
          disabled={!selectedWorkspaceId || pendingDecision !== null}
          onClick={handleAuthorize}
          data-testid="oauthConsentAuthorizeButton"
        >
          Authorize
        </CustomButton>
        {cancelButton}
      </div>
    </ConsentLayout>
  );
};
